/**
 * /product-spec-template — SEO landing page targeting the "product spec
 * template" / "technical spec template" / "feature spec template" cluster.
 *
 * Differentiation angle: a product spec sits closer to implementation than a
 * PRD — it's the document an engineer (or an AI coding agent) actually builds
 * from. This page hands the reader a real spec template (Overview, Functional
 * requirements, Technical approach, Data model / API, Edge cases & error
 * states, Dependencies, Test criteria, Rollout plan), so a searcher gets
 * something usable. Then it shows the failure mode of writing a spec in a
 * doc: it detaches from the PRD and the decisions above it, so when the PRD
 * moves, the spec quietly drifts. In Draftlize each section is a card linked
 * to the upstream decisions, and when a decision changes the spec auto-flags
 * stale — drift_detected — so the coding agent reading over MCP always sees
 * the current spec, not last month's.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and the
 * comparison pages. Chinese variant lives in ProductSpecTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ProductSpecTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/product-spec-template"
      documentTitle="Product Spec Template That Stays in Sync · Draftlize"
      documentDescription="A product spec template you can copy — Overview, Functional requirements, Technical approach, Data model / API, Edge cases & error states, Dependencies, Test criteria, Rollout plan. The catch: a spec in a doc drifts the moment the PRD above it changes. In Draftlize every section is a card linked to the decisions it rests on, so the spec auto-flags stale when an upstream decision moves — and your coding agent always reads the current one."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Product Specs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A product spec template<br/>
            <em>that stays in sync with the decisions behind it.</em>
          </h1>
          <p className="lede">
            You came for a <em>product spec template</em> — the sections, the headings, a structure to build from. It's below; take it. A spec is the document closest to the metal: not "what are we building and why" (that's the PRD) but "here is exactly how it works." The trouble is where it lives. Write it in a doc and it detaches from <a href="/product-requirements-document" style={{ color: 'var(--amber)' }}>the PRD</a> and the decisions above it — so when a call upstream changes, the spec keeps describing a thing you're no longer building, and the engineer (or the AI agent) builds from fiction. Draftlize gives you the same eight sections, makes each one a card wired to the decisions it depends on, and flags the spec stale the moment one of them moves.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole spec template. It works in a doc, a wiki, or a markdown file — it's the shape a good feature or technical spec already takes. Use it as-is. The next section is about what changes when each part stops being a heading in a document and becomes a card that knows what it depends on.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Overview',
              hint: 'One screen',
              body: 'What this feature is and the user-facing outcome it produces, in a few lines. Not the business case — that lives in the PRD — but enough that a reader knows what they\'re about to build before the requirements start.',
            },
            {
              k: 'Functional requirements',
              hint: 'The behavior',
              body: 'What the system does, stated as concrete, checkable behaviors. "When a user submits with an empty title, show an inline error and block save." If a requirement can\'t be turned into a test, it\'s still a wish, not a spec.',
            },
            {
              k: 'Technical approach',
              hint: 'How',
              body: 'The shape of the implementation: the components, the flow, the key choices and why this approach over the obvious alternative. This is where the spec earns its keep — it\'s the part a coding agent reads to avoid re-deciding what was already decided.',
            },
            {
              k: 'Data model / API',
              hint: 'The contract',
              body: 'The tables, fields, types, and endpoints — the surface other code commits to. The most expensive section to get wrong and the first one downstream work hard-codes against, so it\'s the one that most needs to be addressable, not buried.',
            },
            {
              k: 'Edge cases & error states',
              hint: 'The unhappy path',
              body: 'Empty, concurrent, offline, malformed, rate-limited, half-failed. The gap between a demo and a product lives entirely here — and it\'s the section that quietly rots first when the happy-path design changes and nobody revisits the edges.',
            },
            {
              k: 'Dependencies',
              hint: 'What this rests on',
              body: 'The decisions, other specs, and services this spec assumes hold — the pricing call, the auth model, the schema two features over. In a doc this is a list you forget to update. It\'s the section that decides whether the spec stays true when the world above it moves.',
            },
            {
              k: 'Test criteria',
              hint: 'Done means',
              body: 'How you\'ll know it works: the cases that must pass, the behaviors that must hold. Written before the code, this is the line between "shipped" and "shipped, probably." Written after, it just describes whatever the code happens to do.',
            },
            {
              k: 'Rollout plan',
              hint: 'Into the world',
              body: 'Flag, migration, staged release, rollback. How this reaches users without a bad night — and how it comes back if it has to. The section everyone skips and the one the on-call engineer wishes had existed.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why a spec in a doc drifts away from the PRD above it — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a spec <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A spec lives one floor below the PRD</h3>
            <p>A PRD says what to build and why; a spec says exactly how. That's a real dependency: the spec only makes sense while the PRD it implements still holds. But in two separate docs nothing encodes that link, so the spec floats free — and the closer it sits to implementation, the more expensive it is when it's wrong.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change the PRD, nobody re-syncs the spec</h3>
            <p>A decision shifts — the pricing model, the auth flow, a field on the core object. The PRD gets updated, maybe. The spec that built on the old call doesn't, because re-syncing it is manual and competes with shipping. Now Data model and Edge cases describe a system that no longer exists, and no banner says so.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">The agent builds from the stale copy</h3>
            <p>Hand a doc to a coding agent and it builds exactly what the doc says — including the parts that quietly went out of date three commits ago. A spec that can't tell you which of its sections are still trustworthy is worse than no spec: it lends false confidence to the wrong implementation.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>kept in sync.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each section becomes a card, not a heading</h3>
            <p>In Draftlize the spec isn't a single document — each part is a typed card: Overview, Functional requirements, Technical approach, Data model / API, Edge cases, Dependencies, Test criteria, Rollout. Structured, addressable by ID, and citable from a thread or a sibling spec instead of buried under a wiki heading.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change a decision, the spec flags stale</h3>
            <p>Because Dependencies is a real link to the decisions above it — not a list of nouns — changing one of those decisions turns the spec cards that rested on it stale automatically. In a build context we call it drift_detected: the substrate notices the implementation spec no longer matches the call it was written under, the way a build system invalidates everything downstream of a changed file.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your coding agent always reads the current spec</h3>
            <p>Claude Code or Cursor, over MCP, reads the live spec before it writes a line — and sees, on the same surface, which sections are flagged stale and why. The spec stops being a snapshot an agent trusts blindly and becomes context it re-reads every turn, current to the last decision.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A PRD says what to build. A spec says how. The spec is only safe to build from while the decisions above it still hold — so let those decisions flag it when they move.
        </blockquote>
        <cite>Keep the eight sections. Let the substrate keep them in sync.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a product spec?', a: 'A product spec is the document closest to the build: not "what are we making and why" — that is the PRD — but "here is exactly how it works." It describes the behavior, states, edge cases, and constraints in enough detail that an engineer, or an AI agent, can implement it without guessing.' },
            { q: 'What is the difference between a PRD and a spec?', a: 'A PRD sets the intent — the problem, the audience, the outcome. A spec turns that intent into precise behavior. The PRD says "users can recover a deleted project"; the spec says exactly how recovery works, for how long, with what permissions, and what happens at each failure. One decides what to build, the other defines how it works.' },
            { q: 'What should a product spec template include?', a: 'Typically an overview, goals and non-goals, the detailed behavior or user flow, states and edge cases, technical constraints, dependencies, and open questions. The template above covers all of these — use it as-is in a doc, a wiki, or a markdown file.' },
            { q: 'How do you keep a spec from going out of sync?', a: 'The failure is that a spec detaches from the PRD and the decisions above it, so when a call upstream changes, the spec keeps describing something you are no longer building. Draftlize wires each section to the decisions it depends on and flags the spec stale the moment one of them moves, so the engineer or agent never builds from a fossilized version.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your spec <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent draft the spec from your PRD and decisions — then keep every section, and the calls it depends on, in sync for you while you build.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="product-brief" />
    </MarketingShell>
  );
}
